import { useMemo, useState } from "react";
import { SectionCard } from "@/features/power_projects/components/ui";
import { Button } from "@/components/ui/Button";
import type { ZEElementType, ZEParamSpec, ZEParamValue } from "../types";

interface Props {
  elementTypes: ZEElementType[];
  value: Record<string, ZEParamValue>;
  onSave: (next: Record<string, ZEParamValue>) => void;
  saving?: boolean;
  isFullscreen?: boolean;
}

/**
 * Domyślne parametry obmiaru dla całego rysunku.
 *
 * Rysowanie ma być szybkie: typowy przypadek — głębokość wykopu, kategoria gruntu,
 * udział robót ręcznych — ustawia się raz tutaj, a pojedynczy odcinek nadpisuje tylko
 * to, co naprawdę się różni (ikona „Parametry" na mapie). Dzięki temu późniejsza zmiana
 * domyślnej wartości nadal przestawia każdy odcinek, który się z nią nie kłócił.
 *
 * Lista pól to suma `param_schema` wszystkich aktywnych typów elementów — ten sam klucz
 * (np. `kat_gruntu`) pojawia się raz, bo znaczy to samo niezależnie od tego, który
 * element go używa.
 */
export function ZETakeoffDefaultsPanel({
  elementTypes,
  value,
  onSave,
  saving,
  isFullscreen,
}: Props) {
  const [draft, setDraft] = useState<Record<string, ZEParamValue> | null>(null);
  const current = draft ?? value;

  const specs = useMemo(() => {
    const seen = new Map<string, ZEParamSpec>();
    for (const t of elementTypes) {
      for (const spec of t.param_schema ?? []) {
        if (!seen.has(spec.key)) seen.set(spec.key, spec);
      }
    }
    return [...seen.values()];
  }, [elementTypes]);

  if (specs.length === 0) return null;

  const set = (key: string, raw: string, spec: ZEParamSpec) => {
    const next = { ...current };
    if (raw === "") delete next[key];
    else if (spec.type === "number") {
      const n = Number(raw.replace(",", "."));
      if (Number.isFinite(n)) next[key] = n;
      else delete next[key];
    } else if (spec.type === "bool") next[key] = raw === "true";
    else next[key] = raw;
    setDraft(next);
  };

  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(value);

  return (
    <SectionCard
      title="Domyślne parametry obmiaru"
      bodyClassName={isFullscreen ? "overflow-y-auto" : "max-h-[420px] overflow-y-auto"}
    >
      <p className="mb-2 text-[10px] font-mono leading-snug text-[var(--color-text-muted)]">
        Ustawiasz typowy przypadek dla całego rysunku. Odcinek, który się różni,
        nadpiszesz ikoną „Parametry" na mapie. Puste pole = wartość ze słownika.
      </p>
      <div className="space-y-2">
        {specs.map((spec) => {
          const raw = spec.key in current ? String(current[spec.key] ?? "") : "";
          return (
            <div key={spec.key}>
              <label className="text-[11px] text-[var(--color-text-secondary)]">
                {spec.label}
                {spec.unit && spec.type === "number" ? ` [${spec.unit}]` : ""}
              </label>
              {spec.type === "choice" ? (
                <select
                  className="input mt-0.5 w-full py-1 text-[12px]"
                  value={raw}
                  onChange={(e) => set(spec.key, e.target.value, spec)}
                >
                  <option value="">— ze słownika ({String(spec.default ?? "—")}) —</option>
                  {(spec.choices ?? []).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : spec.type === "bool" ? (
                <select
                  className="input mt-0.5 w-full py-1 text-[12px]"
                  value={spec.key in current ? String(Boolean(current[spec.key])) : ""}
                  onChange={(e) => set(spec.key, e.target.value, spec)}
                >
                  <option value="">
                    — ze słownika ({spec.default ? "tak" : "nie"}) —
                  </option>
                  <option value="true">tak</option>
                  <option value="false">nie</option>
                </select>
              ) : (
                <input
                  type="number"
                  step="any"
                  className="input mt-0.5 w-full py-1 font-mono text-[12px]"
                  value={raw}
                  placeholder={String(spec.default ?? "")}
                  onChange={(e) => set(spec.key, e.target.value, spec)}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          disabled={!dirty || saving}
          loading={saving}
          onClick={() => draft && onSave(draft)}
        >
          Zapisz domyślne
        </Button>
        {dirty && (
          <button
            className="text-[11px] text-[var(--color-text-tertiary)] hover:underline"
            onClick={() => setDraft(null)}
          >
            Cofnij zmiany
          </button>
        )}
      </div>
    </SectionCard>
  );
}
